import { useEffect, useRef } from 'react';
import { Routes, Route, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTelegramAuth } from './hooks/useTelegramAuth.js';
import { useUserEvents } from './hooks/useUserEvents';
import { ImageFeatureRoute, ImageSkuRoute } from './components/ImageFeatureRoute.js';
import { parseStartParam } from './lib/start-param-route.js';
import { useSyncI18nLanguage } from './i18n/useSyncI18nLanguage.js';
import { useAuth } from './store/auth.js';
import { Splash } from './screens/onboarding/Splash.js';
import { LanguagePick } from './screens/onboarding/LanguagePick.js';
import { AgePrivacy } from './screens/onboarding/AgePrivacy.js';
import { BirthData } from './screens/onboarding/BirthData.js';
import { AvatarsCategory } from './screens/AvatarsCategory.js';
import { SkuDetail } from './screens/SkuDetail.js';
import { Home } from './screens/home/Home.js';
import { MeOverview } from './screens/me/Overview.js';
import { Purchases as MePurchases } from './screens/me/Purchases.js';
import { Waitlist as MeWaitlist } from './screens/me/Waitlist.js';
import { Couples as MeCouples } from './screens/me/Couples.js';
import { Friends as MeFriends } from './screens/me/Friends.js';
import { NatalChart as MeNatalChart } from './screens/me/NatalChart.js';
import { ChartsList as MeChartsList } from './screens/me/charts/ChartsList.js';
import { ChartNew as MeChartNew } from './screens/me/charts/ChartNew.js';
import { ChartEdit as MeChartEdit } from './screens/me/charts/ChartEdit.js';
import { ChartPage as MeChartPage } from './screens/me/charts/ChartPage.js';
import { StarPersonaGallery as MeStarPersonaGallery } from './screens/me/StarPersonaGallery.js';
import { DailyBrief } from './screens/me/DailyBrief.js';
import { Settings } from './screens/Settings.js';
import { Notifications as SettingsNotifications } from './screens/settings/Notifications.js';
import { Privacy as SettingsPrivacy } from './screens/settings/Privacy.js';
import { Account as SettingsAccount } from './screens/settings/Account.js';
import { Language as SettingsLanguage } from './screens/settings/Language.js';
import { PushConsent as PushConsentOnboarding } from './screens/onboarding/PushConsent.js';
import { TabShell } from './components/chrome/TabShell.js';
import { MoonPhaseEntry } from './components/flows/MoonPhase/MoonPhaseEntry.js';
import { MoonPhaseComputing } from './components/flows/MoonPhase/MoonPhaseComputing.js';
import { MoonPhaseReveal } from './components/flows/MoonPhase/MoonPhaseReveal.js';
import { MoonPhaseShare } from './components/flows/MoonPhase/MoonPhaseShare.js';
import { AvatarFlow } from './components/avatarFlow/AvatarFlow.js';
import { CompatFlow } from './components/flows/Compat/CompatFlow.js';
import { CompatResult } from './screens/CompatResult.js';
import { AvatarPackResult } from './screens/me/AvatarPackResult.js';
import { PastLivesResult } from './screens/me/PastLivesResult.js';
import { TextResult } from './screens/me/TextResult.js';
import { CoupleFlow } from './components/flows/Couple/CoupleFlow.js';
import { PastLivesFlow } from './components/pastLivesFlow/PastLivesFlow.js';
import { Computing } from './screens/onboarding/Computing.js';
import { InfoReveal } from './screens/onboarding/InfoReveal.js';
import { PickPersona } from './screens/onboarding/PickPersona.js';
import { RootRedirect } from './components/RootRedirect.js';
import { PublicProfile } from './screens/PublicProfile.js';
import { SelfieIntro } from './screens/onboarding/SelfieIntro.js';
import { CameraCapture } from './screens/onboarding/CameraCapture.js';
import { AddingToWorld } from './screens/onboarding/AddingToWorld.js';
import { PersonalReveal } from './screens/onboarding/PersonalReveal.js';
import { CheckForMe } from './screens/check/CheckForMe.js';
import { Lucky } from './screens/lucky/Lucky.js';
import { AskQuestion } from './screens/ask/AskQuestion.js';
import { When } from './screens/when/When.js';
import { ChatEntry } from './screens/chat/ChatEntry.js';
import { CoursePage } from './screens/course/CoursePage.js';
import { AboutCoursesPage } from './screens/course/AboutCoursesPage.js';
import { LessonPage } from './screens/course/LessonPage.js';
import { CourseChat } from './screens/course/CourseChat';
import { ExamPage } from './screens/course/ExamPage.js';
import { AstrologerChat } from './screens/chat/AstrologerChat.js';

function AuthGateLayout(): JSX.Element {
  useTelegramAuth();
  useUserEvents();
  useSyncI18nLanguage();
  const { t } = useTranslation('common');
  const status = useAuth((s) => s.status);
  const error = useAuth((s) => s.error);
  const pendingDeletion = useAuth((s) => s.pendingDeletion);
  if (status === 'pending_deletion' && pendingDeletion) {
    return <PendingDeletionBanner deletedAt={pendingDeletion.deletedAt} gracePeriodDays={pendingDeletion.gracePeriodDays} />;
  }
  if (status === 'error') return <div className="p-8 text-red-400">{t('errors.generic')}: {error}</div>;
  return <Outlet />;
}

function PendingDeletionBanner({
  deletedAt,
  gracePeriodDays,
}: {
  deletedAt: string;
  gracePeriodDays: number;
}): JSX.Element {
  const { t } = useTranslation('settings');
  const purgeDate = new Date(new Date(deletedAt).getTime() + gracePeriodDays * 24 * 60 * 60 * 1000);
  return (
    <div className="p-8 text-amber-200" role="alert" data-testid="pending-deletion-banner">
      <h1 className="mb-4 text-xl font-semibold">{t('account.pendingDeletion.title')}</h1>
      <p className="mb-2">
        {t('account.pendingDeletion.body', { purgeDate: purgeDate.toUTCString().slice(0, 16) })}
      </p>
      <p>{t('account.pendingDeletion.supportHint')}</p>
    </div>
  );
}

export default function App(): JSX.Element {
  const navigate = useNavigate();
  const startHandled = useRef(false);
  useEffect(() => {
    if (startHandled.current) return;
    const param = (window.Telegram as { WebApp?: { initDataUnsafe?: { start_param?: string } } } | undefined)?.WebApp?.initDataUnsafe?.start_param ?? null;
    const route = parseStartParam(param);
    if (route) { startHandled.current = true; navigate(route.path); }
  }, [navigate]);

  return (
    <Routes>
      {/* Public — no Telegram auth required */}
      <Route path="/c/:userId" element={<PublicProfile />} />

      {/* Everything else — Telegram auth gate */}
      <Route element={<AuthGateLayout />}>
        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Onboarding — no NavTabs shell */}
        <Route path="/onboarding/splash" element={<Splash />} />
        <Route path="/onboarding/language" element={<LanguagePick />} />
        <Route path="/onboarding/age" element={<AgePrivacy />} />
        <Route path="/onboarding/birth" element={<BirthData />} />
        <Route path="/onboarding/computing" element={<Computing />} />
        <Route path="/onboarding/info-reveal" element={<InfoReveal />} />
        <Route path="/onboarding/pick" element={<ImageFeatureRoute fallback="/onboarding/push"><PickPersona /></ImageFeatureRoute>} />
        <Route path="/onboarding/selfie" element={<ImageFeatureRoute fallback="/onboarding/push"><SelfieIntro /></ImageFeatureRoute>} />
        <Route path="/onboarding/capture" element={<ImageFeatureRoute fallback="/onboarding/push"><CameraCapture /></ImageFeatureRoute>} />
        <Route path="/onboarding/adding" element={<ImageFeatureRoute fallback="/onboarding/push"><AddingToWorld /></ImageFeatureRoute>} />
        <Route path="/onboarding/personal" element={<ImageFeatureRoute fallback="/onboarding/push"><PersonalReveal /></ImageFeatureRoute>} />

        {/* Main tab routes — wrapped in TabShell for NavTabs chrome */}
        <Route element={<TabShell />}>
          <Route path="/home" element={<Home />} />
          <Route path="/course" element={<CoursePage />} />
          <Route path="/me" element={<MeOverview />} />
        </Route>

        {/* The catalog is hidden: old links and buttons land on the main screen. */}
        <Route path="/catalog" element={<Navigate to="/home" replace />} />

        {/* Me sub-pages — OUTSIDE TabShell (sub-pages, not tabs) */}
        <Route path="/me/purchases" element={<ImageFeatureRoute fallback="/me"><MePurchases /></ImageFeatureRoute>} />
        <Route path="/me/waitlist" element={<MeWaitlist />} />
        <Route path="/me/couples" element={<MeCouples />} />
        <Route path="/me/friends" element={<MeFriends />} />
        <Route path="/me/natal-chart" element={<MeNatalChart />} />
        <Route path="/me/charts" element={<MeChartsList />} />
        <Route path="/me/charts/new" element={<MeChartNew />} />
        <Route path="/me/charts/:id" element={<MeChartPage />} />
        <Route path="/me/charts/:id/edit" element={<MeChartEdit />} />
        <Route path="/me/star-persona" element={<ImageFeatureRoute fallback="/me"><MeStarPersonaGallery /></ImageFeatureRoute>} />
        <Route path="/me/daily-brief" element={<DailyBrief />} />
        <Route path="/chat" element={<AstrologerChat />} />
        <Route path="/chat/open" element={<ChatEntry />} />
        <Route path="/check" element={<CheckForMe />} />
        <Route path="/lucky" element={<Lucky />} />
        <Route path="/ask" element={<AskQuestion />} />
        <Route path="/when" element={<When />} />
        <Route path="/course/about" element={<AboutCoursesPage />} />
        <Route path="/course/chat" element={<CourseChat />} />
        <Route path="/course/lesson/:code" element={<LessonPage />} />
        <Route path="/course/exam/:code" element={<ExamPage />} />

        {/* Settings sub-pages — OUTSIDE TabShell (sub-page, not a tab) */}
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/notifications" element={<SettingsNotifications />} />
        <Route path="/settings/privacy" element={<SettingsPrivacy />} />
        <Route path="/settings/account" element={<SettingsAccount />} />
        <Route path="/settings/language" element={<SettingsLanguage />} />
        {/* OB6 — push consent onboarding step (new OB6Push design) */}
        <Route path="/onboarding/push" element={<PushConsentOnboarding />} />

        {/* Avatars category picker — collapses 6 avatar tiers from the catalog */}
        <Route path="/avatars" element={<ImageFeatureRoute fallback="/home"><AvatarsCategory /></ImageFeatureRoute>} />

        {/* SKU detail — no NavTabs (immersive) */}
        <Route path="/sku/:code" element={<ImageSkuRoute fallback="/catalog"><SkuDetail /></ImageSkuRoute>} />

        {/* Moon Phase Soulmate flow (T22) — no NavTabs (immersive flow) */}
        <Route path="/flow/moon-phase-soulmate" element={<ImageFeatureRoute fallback="/catalog"><MoonPhaseEntry /></ImageFeatureRoute>} />
        <Route path="/flow/moon-phase-soulmate/computing" element={<ImageFeatureRoute fallback="/catalog"><MoonPhaseComputing /></ImageFeatureRoute>} />
        <Route path="/flow/moon-phase-soulmate/reveal" element={<ImageFeatureRoute fallback="/catalog"><MoonPhaseReveal /></ImageFeatureRoute>} />
        <Route path="/flow/moon-phase-soulmate/share" element={<ImageFeatureRoute fallback="/catalog"><MoonPhaseShare /></ImageFeatureRoute>} />

        {/* Generic avatar-SKU flow (6 LIVE SKUs share this route) */}
        <Route path="/flow/avatar/:code" element={<ImageFeatureRoute fallback="/home"><AvatarFlow /></ImageFeatureRoute>} />

        {/* Compatibility Check flow (T029) */}
        <Route path="/flow/compat" element={<CompatFlow />} />

        {/* Cosmic Gift — same flow in gift mode (gifting_crush, anonymous, 50⭐) */}
        <Route path="/flow/gift" element={<CompatFlow gift />} />

        {/* Compatibility result by id — bot "Open in Mini App" deep link target */}
        <Route path="/compat/:id" element={<CompatResult />} />

        {/* My Rites — view a purchased avatar pack / past-lives run by id */}
        <Route path="/me/avatar-pack/:id" element={<ImageFeatureRoute fallback="/me"><AvatarPackResult /></ImageFeatureRoute>} />
        <Route path="/me/past-lives/:id" element={<ImageFeatureRoute fallback="/me"><PastLivesResult /></ImageFeatureRoute>} />
        <Route path="/me/result/:id" element={<TextResult />} />

        {/* Couple Portrait flow (spec 018) */}
        <Route path="/flow/couple" element={<ImageFeatureRoute fallback="/home"><CoupleFlow /></ImageFeatureRoute>} />

        {/* Past Lives Pack flow (spec 021) */}
        <Route path="/flow/past-lives/:code" element={<ImageSkuRoute fallback="/home"><PastLivesFlow /></ImageSkuRoute>} />
      </Route>
    </Routes>
  );
}
